import { io } from "socket.io-client";
import api from "../api/api";
import { getLiveSnapshot } from "../api/liveMatchApi";
import { eventIdentity, normalizeLiveEvent, normalizeSnapshot, normalizeTimer, payloadMatchId, payloadRevision } from "./liveMatchNormalizer";
import SOCKET_EVENTS from "./socketEvents";
import { readLedContentMode, readOverlayContentMode } from "./overlayContentMode";
import { readRetryAfterMs } from "./pollBackoff";

const SOCKET_URL = process.env.REACT_APP_LIVE_SOCKET_URL || process.env.REACT_APP_API_URL || "https://elitlig-api-88a866b7a4da.herokuapp.com";
const SOCKET_ENABLED = process.env.REACT_APP_LIVE_SOCKET_ENABLED !== "false";
const GRACE_MS = Number(process.env.REACT_APP_LIVE_SOCKET_GRACE_MS) || 5000;
const RELEASE_GRACE_MS = Number(process.env.REACT_APP_LIVE_RELEASE_GRACE_MS) || 3000;
const POLL_MS = Number(process.env.REACT_APP_LIVE_FALLBACK_POLL_MS) || 25000;
const HIDDEN_MS = Number(process.env.REACT_APP_LIVE_HIDDEN_POLL_MS) || 90000;
const DEBUG = process.env.NODE_ENV !== "production" || process.env.REACT_APP_LIVE_DEBUG === "true";
const channels = new Map();

// Sekme başına TEK socket bağlantısı. Önceden her maç kanalı kendi io()
// bağlantısını açıyordu: ana sayfadaki 8 canlı kart = ziyaretçi başına 8
// WebSocket. Sunucu yeniden başladığında hepsi aynı anda yeniden bağlanıp
// her biri ayrı anlık görüntü istiyordu. Kanallar artık bu ortak bağlantı
// üzerinde yalnızca kendi maç odalarına katılır; olayları matchId ile ayırır.
let sharedSocket = null;
let sharedSocketRefs = 0;
const acquireSocket = () => {
  if (!sharedSocket) {
    sharedSocket = io(SOCKET_URL, {
      transports: ["websocket", "polling"], withCredentials: true, reconnection: true, reconnectionAttempts: Infinity,
      // Yeniden başlatma sonrası tüm ziyaretçilerin aynı saniyede dönmemesi için
      // ilk bekleme daha uzun ve rastgele dağıtılmış.
      reconnectionDelay: 3000, reconnectionDelayMax: 30000, randomizationFactor: 0.5, timeout: 20000,
    });
  }
  sharedSocketRefs += 1;
  return sharedSocket;
};
const releaseSocket = (socket) => {
  if (!socket || socket !== sharedSocket) return;
  sharedSocketRefs = Math.max(0, sharedSocketRefs - 1);
  if (sharedSocketRefs) return;
  socket.disconnect();
  sharedSocket = null;
};
// Eşzamanlı istemcilerin aynı anda istek atmaması için ±%20 dağıtım.
const jitter = (ms) => Math.round(ms * (0.8 + Math.random() * 0.4));

const initial = (matchId) => ({
  matchId: String(matchId), match: null, score: { home: 0, away: 0 },
  homeTeam: { id: null, name: "", logo: "" }, awayTeam: { id: null, name: "", logo: "" },
  timer: { running: false, baseMs: 0, baseAt: Date.now(), clockOffsetMs: 0, revision: 0 },
  events: [], lineups: { home: [], away: [] }, lineupVersion: 0,
  notificationQueue: [], currentNotification: null,
  // Yayın overlay modları YALNIZCA modu açıkça taşıyan mesajlardan güncellenir.
  // `match` alanı kısmi payload'larla (skor, durum) birleştirildiği için orada
  // kalan eski overlay_status değeri geçerli mod olarak okunmamalı: gol sonrası
  // gelen skor mesajı skorboardu bir anlığına maç önü görseline döndürüyordu.
  overlayMode: "", ledMode: "",
  connectionStatus: SOCKET_ENABLED ? "connecting" : "fallback", isSocketConnected: false,
  isFallbackPolling: false, lastRevision: 0, lastUpdatedAt: null, error: null,
});

const debug = () => {
  if (!DEBUG || typeof window === "undefined") return null;
  window.__ELITLIG_LIVE_DEBUG__ ||= { socketConnectionCount: 0, reconnectCount: 0, duplicateEventCount: 0, liveHttpRequests: [] };
  return window.__ELITLIG_LIVE_DEBUG__;
};

export class LiveChannel {
  constructor(matchId) {
    this.matchId = String(matchId); this.state = initial(matchId); this.listeners = new Set(); this.refs = 0;
    this.socket = null; this.pollTimer = null; this.graceTimer = null; this.abort = null; this.requestReason = null; this.etag = null;
    this.failures = 0; this.disposed = false; this.seenEvents = new Set(); this.started = false; this.notificationTimer = null;
    this.onVisibility = () => { if (!document.hidden && !this.state.isSocketConnected) this.refreshSnapshot("visible"); };
    this.onOnline = () => { if (!this.state.isSocketConnected) this.refreshSnapshot("online"); };
  }
  emit(patch) { this.state = { ...this.state, ...patch }; this.listeners.forEach(fn => fn()); this.updateDebug(); }
  updateDebug() { const d = debug(); if (d) Object.assign(d, { currentRoom: `match:${this.matchId}`, pollingActive: this.state.isFallbackPolling, lastSnapshotAt: this.state.lastUpdatedAt, lastRevision: this.state.lastRevision }); }
  subscribe = (fn) => { this.listeners.add(fn); this.retain(); return () => { this.listeners.delete(fn); this.release(); }; };
  getSnapshot = () => this.state;
  retain() { clearTimeout(this.releaseTimer); this.releaseTimer = null; this.refs += 1; if (!this.started) this.start(); }
  // Son abone ayrılınca kanal hemen değil kısa bir bekleme sonrası kapatılır:
  // bir kartın söküp yeniden kurulması (sekme geçişi, yeniden çizim) socket'i
  // kapatıp yeniden açmasın ve anlık görüntü isteğini yinelemesin.
  release() {
    this.refs = Math.max(0, this.refs - 1);
    if (this.refs) return;
    clearTimeout(this.releaseTimer);
    this.releaseTimer = setTimeout(() => { this.releaseTimer = null; if (!this.refs) this.stop(); }, RELEASE_GRACE_MS);
  }
  start() {
    this.started = true; this.disposed = false;
    window.addEventListener("online", this.onOnline); document.addEventListener("visibilitychange", this.onVisibility);
    // Socket connectivity must not suppress the one-shot metadata bootstrap.
    this.refreshSnapshot("bootstrap");
    if (!SOCKET_ENABLED) { this.startFallback(0); return; }
    this.graceTimer = setTimeout(() => this.startFallback(0), GRACE_MS);
    this.socket = acquireSocket();
    const d = debug(); if (d) d.socketConnectionCount += 1;
    const onConnect = () => {
      const wasConnected = this.state.lastUpdatedAt != null; this.stopFallback(); clearTimeout(this.graceTimer);
      this.emit({ connectionStatus: "connected", isSocketConnected: true, error: null });
      this.socket.emit(SOCKET_EVENTS.JOIN_MATCH, { matchId: Number(this.matchId) });
      this.socket.emit(SOCKET_EVENTS.RESYNC, { matchId: Number(this.matchId), version: this.state.lastRevision });
      if (wasConnected && d) d.reconnectCount += 1;
    };
    this.handlers = {
      connect: onConnect,
      disconnect: () => { this.emit({ connectionStatus: "disconnected", isSocketConnected: false }); this.startFallback(jitter(GRACE_MS)); },
      connect_error: () => this.emit({ connectionStatus: "reconnecting", isSocketConnected: false }),
    };
    const events = {
      live_match_snapshot: p => this.applySnapshot(p), "live-match:update": p => this.applySnapshot(p), [SOCKET_EVENTS.STATE_UPDATED]: p => this.applySnapshot(p),
      live_match_score_updated: p => this.applyDelta("score", p), [SOCKET_EVENTS.SCORE_UPDATED]: p => this.applyDelta("score", p),
      live_match_timer_updated: p => this.applyDelta("timer", p), [SOCKET_EVENTS.TIMER_UPDATED]: p => this.applyDelta("timer", p),
      live_match_event_created: p => this.applyDelta("event-created", p), [SOCKET_EVENTS.EVENT_CREATED]: p => this.applyDelta("event-created", p),
      live_match_event_updated: p => this.applyDelta("event-updated", p), [SOCKET_EVENTS.EVENT_UPDATED]: p => this.applyDelta("event-updated", p),
      live_match_event_deleted: p => this.applyDelta("event-deleted", p), [SOCKET_EVENTS.EVENT_DELETED]: p => this.applyDelta("event-deleted", p),
      live_match_lineup_updated: p => this.applyDelta("lineup", p), [SOCKET_EVENTS.LINEUP_UPDATED]: p => this.applyDelta("lineup", p),
      live_match_status_updated: p => this.applyDelta("status", p), [SOCKET_EVENTS.STATUS_UPDATED]: p => this.applyDelta("status", p),
    };
    Object.assign(this.handlers, events);
    Object.entries(this.handlers).forEach(([name, fn]) => this.socket.on(name, fn));
    // Ortak bağlantı zaten açıksa "connect" yeniden gelmez; odaya hemen katıl.
    if (this.socket.connected) onConnect();
  }
  // Bağlantı birden çok maç odasıyla paylaşıldığı için maç kimliği taşımayan
  // ya da başka maça ait mesaj bu kanala uygulanmaz.
  valid(payload) { const id = payloadMatchId(payload); return id != null && String(id) === this.matchId; }
  stale(payload) { const revision = payloadRevision(payload); return revision != null && revision < this.state.lastRevision; }
  // Modu yalnızca payload'ın kendisi geçerli bir değer taşıyorsa döndürür;
  // depodaki (muhtemelen bayat) `match` alanına asla düşmez.
  explicitModes(payload) {
    const patch = {};
    const overlayMode = readOverlayContentMode(payload);
    const ledMode = readLedContentMode(payload);
    if (overlayMode) patch.overlayMode = overlayMode;
    if (ledMode) patch.ledMode = ledMode;
    return patch;
  }
  applySnapshot(payload) {
    if (!this.valid(payload) || this.stale(payload)) return;
    const next = normalizeSnapshot(payload, { ...this.state, revision: this.state.lastRevision });
    this.seenEvents = new Set(next.events.map(eventIdentity).filter(Boolean));
    this.emit({ ...next, ...this.explicitModes(payload), lastRevision: next.revision, lastUpdatedAt: Date.now(), error: null });
  }
  applyDelta(kind, payload) {
    if (!this.valid(payload) || this.stale(payload)) return;
    const revision = payloadRevision(payload) ?? this.state.lastRevision;
    if (kind === "timer") {
      const timer = normalizeTimer(payload, this.state.timer);
      // Devre sayaç güncellemesiyle gelir; skorboard devreyi maç verisinden
      // okuduğu için oraya da yazılır (yoksa bir sonraki anlık görüntüye kadar
      // "1. YARI" görünmeye devam ediyordu).
      const periodPatch = timer.period != null && timer.period !== this.state.timer?.period
        ? { match: { ...(this.state.match || {}), period: timer.period, live_period: timer.period, livePeriod: timer.period } }
        : {};
      this.emit({ timer, ...periodPatch, lastRevision: revision, lastUpdatedAt: Date.now() });
    }
    else if (kind === "score") this.emit({ score: normalizeSnapshot(payload, this.state).score, lastRevision: revision, lastUpdatedAt: Date.now() });
    else if (kind === "lineup") this.emit({ lineups: normalizeSnapshot({ lineups: payload.lineups || payload.lineup || payload }, this.state).lineups, lineupVersion: payload.lineupVersion ?? revision, lastRevision: revision });
    else if (kind === "status") this.emit({ match: { ...this.state.match, ...payload }, ...this.explicitModes(payload), lastRevision: revision });
    else {
      const event = normalizeLiveEvent(payload); const id = eventIdentity(event); let events = this.state.events.slice();
      if (kind === "event-deleted") events = events.filter(item => eventIdentity(item) !== id);
      else { const index = events.findIndex(item => eventIdentity(item) === id); if (index >= 0) { events[index] = { ...events[index], ...event }; if (kind === "event-created") { const d = debug(); if (d) d.duplicateEventCount += 1; } } else { events.push(event); if (kind === "event-created") this.enqueueNotification(event); } }
      this.emit({ events, lastRevision: revision, lastUpdatedAt: Date.now() });
    }
  }
  enqueueNotification(event) {
    if (!eventIdentity(event) || this.seenEvents.has(eventIdentity(event))) return;
    this.seenEvents.add(eventIdentity(event));
    const queue = [...this.state.notificationQueue, event];
    this.emit({ notificationQueue: queue });
    this.showNextNotification();
  }
  showNextNotification() {
    if (this.state.currentNotification || !this.state.notificationQueue.length) return;
    const [currentNotification, ...notificationQueue] = this.state.notificationQueue;
    this.emit({ currentNotification, notificationQueue });
    const code = currentNotification.eventCode || "";
    const duration = code.includes("GOAL") || code.includes("GOL") ? 5000 : 4000;
    clearTimeout(this.notificationTimer);
    this.notificationTimer = setTimeout(() => { this.emit({ currentNotification: null }); this.showNextNotification(); }, duration);
  }
  startFallback(delay = GRACE_MS) { clearTimeout(this.graceTimer); this.graceTimer = setTimeout(() => { if (this.disposed || this.state.isSocketConnected) return; this.emit({ connectionStatus: "fallback", isFallbackPolling: true }); this.refreshSnapshot("fallback"); }, delay); }
  // Socket yeniden bağlandığında süren anlık görüntü isteği İPTAL EDİLMEZ:
  // sunucu yeniden başladıktan hemen sonra tüm istemciler aynı anda bağlanıp
  // isteklerini kesiyordu (Heroku H27). Yanıt gelince zaten işlenir.
  stopFallback() { clearTimeout(this.pollTimer); clearTimeout(this.graceTimer); this.pollTimer = null; if (this.state.isFallbackPolling) this.emit({ isFallbackPolling: false }); }
  schedulePoll() { clearTimeout(this.pollTimer); if (this.disposed || this.state.isSocketConnected) return; const base = document.hidden ? HIDDEN_MS : POLL_MS; const delay = Math.max(jitter(Math.min(120000, base * Math.pow(2, Math.min(this.failures, 3)))), this.failures ? this.retryAfterMs || 0 : 0); this.pollTimer = setTimeout(() => this.refreshSnapshot("fallback"), delay); }
  async refreshSnapshot(reason = "manual") {
    if (this.disposed || (reason === "fallback" && this.state.isSocketConnected) || navigator.onLine === false) { if (reason === "fallback") this.schedulePoll(); return null; }
    // Süren istek varsa yenisi açılmaz (eskisini kesmek H27 üretir).
    if (this.abort) { if (reason === "fallback") this.schedulePoll(); return null; }
    const controller = new AbortController(); this.abort = controller; this.requestReason = reason;
    const d = debug(); if (d) { d.liveHttpRequests.push(Date.now()); d.liveHttpRequests = d.liveHttpRequests.filter(at => at > Date.now() - 60000); }
    try {
      const data = process.env.REACT_APP_LIVE_SNAPSHOT_URL
        ? (await api.get(process.env.REACT_APP_LIVE_SNAPSHOT_URL.replace(":matchId", this.matchId), { signal: controller.signal, timeout: 25000 })).data
        : await getLiveSnapshot(this.matchId, { signal: controller.signal, force: reason !== "bootstrap", reason });
      this.applySnapshot(data);
      this.failures = 0; this.retryAfterMs = 0; return this.state;
    } catch (error) {
      // Sunucu 429/503 ile Retry-After önerdiyse (ör. veritabanı kotası dolu)
      // sonraki yedek yoklama en az o kadar bekler.
      if (error?.code !== "ERR_CANCELED") { this.failures += 1; this.retryAfterMs = readRetryAfterMs(error); this.emit({ error }); }
      return null;
    }
    finally { if (this.abort === controller) { this.abort = null; this.requestReason = null; } if (reason === "fallback") this.schedulePoll(); }
  }
  stop() {
    clearTimeout(this.releaseTimer); this.releaseTimer = null;
    this.disposed = true; this.started = false; clearTimeout(this.notificationTimer); this.abort?.abort(); this.abort = null; this.requestReason = null; this.stopFallback(); window.removeEventListener("online", this.onOnline); document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.socket) { if (this.socket.connected) this.socket.emit(SOCKET_EVENTS.LEAVE_MATCH, { matchId: Number(this.matchId) }); Object.entries(this.handlers || {}).forEach(([name, fn]) => this.socket.off(name, fn)); this.handlers = null; releaseSocket(this.socket); this.socket = null; const d = debug(); if (d) d.socketConnectionCount = Math.max(0, d.socketConnectionCount - 1); }
    channels.delete(this.matchId);
  }
}

export const getLiveMatchChannel = (matchId) => {
  if (matchId == null || matchId === "") return null;
  const key = String(matchId); if (!channels.has(key)) channels.set(key, new LiveChannel(key)); return channels.get(key);
};
export const __resetLiveMatchChannels = () => { channels.forEach(channel => channel.stop()); channels.clear(); };
